import React from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import MomentumLogo from '../../images/MomentumAMP Logo.png';
import '../../styles/pages.css';

const FEATURES = [
  { icon: 'pi-file-edit', label: 'Certificates', desc: 'Issue and manage COIs instantly' },
  { icon: 'pi-send',      label: 'Requests',     desc: 'Track and fulfill holder requests' },
  { icon: 'pi-chart-bar', label: 'Reports',      desc: 'Insights across policies and clients' },
];

const STEPS = [
  { label: 'Validating your credentials', icon: 'pi-lock' },
  { label: 'Setting up your workspace',   icon: 'pi-briefcase' },
  { label: 'Loading certificates',        icon: 'pi-file-check' },
];

export default function BridgePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>('');

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const trackClickForGenerateQuote = params.get('trackClickForGenerateQuote');
    const policyid = params.get('policyid');
    const policy = decodePolicyParam(params.get('policy') ?? '');

    if (!token) {
      setError('Missing token in bridge URL.');
      setLoading(false);
      return;
    }

    validateNowcertsToken(token, trackClickForGenerateQuote ?? 'false', policyid ?? '', policy ?? '', login, navigate, setLoading, setError);
  }, [location.search, login, navigate]);

  const showError = !loading && !!error;

  return (
    <div className="loginShell">
      {/* Left branding panel — identical to LoginPage */}
      <div className="loginShell__left">
        <div className="loginShell__leftInner">
          <div className="loginShell__leftLogo">
            <img src={MomentumLogo} alt="Momentum AMP" className="loginShell__leftLogoImg" />
          </div>
          <div className="loginShell__leftTagline">Certificate of Insurance Portal</div>
          <p className="loginShell__leftSub">
            Your one-stop portal for managing certificates of insurance, requests, and compliance documents.
          </p>
          <div className="loginShell__features">
            {FEATURES.map((f) => (
              <div key={f.icon} className="loginShell__featureRow">
                <div className="loginShell__featureIcon">
                  <i className={`pi ${f.icon}`} />
                </div>
                <div>
                  <div className="loginShell__featureLabel">{f.label}</div>
                  <div className="loginShell__featureDesc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="loginShell__blob loginShell__blob--1" />
        <div className="loginShell__blob loginShell__blob--2" />
      </div>

      {/* Right panel */}
      <div className="loginShell__right">
        <div className="loginShell__panel">
          {/* mobile logo */}
          <div className="loginShell__mobileLogoWrap">
            <img src={MomentumLogo} alt="Momentum AMP" className="loginShell__mobileLogo" />
          </div>

          {showError ? (
            /* ── Error state ── */
            <>
              <div className="bridgePage__errorIcon">
                <i className="pi pi-exclamation-triangle" />
              </div>
              <div className="loginShell__headline">Unable to sign you in</div>
              <p className="loginShell__subtext">{error}</p>
              <p className="bridgePage__errorHint">
                Close this window and relaunch from NowCerts, or return to sign in.
              </p>
              <div className="loginShell__actions">
                <button
                  type="button"
                  className="loginShell__primaryBtn"
                  onClick={() => navigate('/', { replace: true })}
                >
                  <i className="pi pi-arrow-left" />
                  Back to sign in
                </button>
              </div>
            </>
          ) : (
            /* ── Loading state ── */
            <>
              <div className="bridgePage__cardLogo">
                <img src={MomentumLogo} alt="Momentum AMP" className="loginShell__cardLogoImg" />
              </div>

              <div className="loginShell__ssoBadge">
                <i className="pi pi-lock" />
                NowCerts SSO
              </div>

              <div className="loginShell__headline bridgePage__headline">
                Signing you in…
              </div>
              <p className="loginShell__subtext">
                Securely connecting your NowCerts account and preparing your workspace.
              </p>

              <div className="bridgePage__spinnerWrap">
                <div className="loginShell__ssoSpinner">
                  <span className="loginShell__ssoRing" />
                </div>
              </div>

              <div className="loginShell__ssoSteps">
                {STEPS.map((s, i) => (
                  <div
                    key={i}
                    className="loginShell__ssoStep"
                    style={{ animationDelay: `${i * 0.45}s` }}
                  >
                    <i className={`pi ${s.icon} loginShell__ssoStepIcon`} />
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function validateNowcertsToken(
  token: string,
  trackClickForGenerateQuote: string,
  policyid: string,
  policy: string,
  login: (email: string) => void,
  navigate: (path: string, options?: { replace?: boolean }) => void,
  setLoading: (v: boolean) => void,
  setError: (msg: string) => void,
) {
  setLoading(true);

  const config = {
    method: 'get',
    url: `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/validateNowcertsToken?token=${encodeURIComponent(
      token,
    )}&trackClick=${encodeURIComponent(trackClickForGenerateQuote)}&policyid=${encodeURIComponent(policyid)}`,
  } as const;

  axios(config)
    .then((response: any) => {
      if (!response.data || !response.data.token) {
        clearAuthStorage();
        setError('Your session has expired or is invalid.');
        setLoading(false);
        return;
      }

      localStorage.setItem('SocialLogin', 'true');
      localStorage.setItem('igtoken', response.data.token);
      localStorage.setItem('tenantId', response.data.tenantId);
      localStorage.setItem('userName', response.data.userName);
      localStorage.setItem('RoleId', response.data.roleId);
      localStorage.setItem('NowcertsAgentId', response.data.nowCertsAgentId);
      localStorage.setItem('AvatarName', response.data.avatarName);
      localStorage.setItem('firstName', response.data.firstName ?? '');
      localStorage.setItem('lastName', response.data.lastName ?? '');
      localStorage.setItem('workEmail', response.data.workEmail ?? '');

      getNcToken(response.data.userName, response.data.token, policy, login, navigate, setLoading, setError);
    })
    .catch((error: any) => {
      setLoading(false);
      const status = error?.response?.status;

      if (status === 401) {
        clearAuthStorage();
        setError('Your session has expired. Please sign in again.');
      } else {
        setError('Unknown error while validating Nowcerts token.');
      }
    });
}

function getNcToken(
  ncSetupUserName: string,
  igToken: string,
  policy: string,
  login: (email: string) => void,
  navigate: (path: string, options?: { replace?: boolean }) => void,
  setLoading: (v: boolean) => void,
  setError: (msg: string) => void,
) {
  const data = JSON.stringify({
    NCUserName: ncSetupUserName,
  });

  const config = {
    method: 'post',
    url: `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/verifyMomentumCreds`,
    headers: {
      Authorization: `Bearer ${igToken}`,
      'Content-Type': 'application/json',
    },
    data,
  } as const;

  axios(config)
    .then((response: any) => {
      if (response.data) {
        localStorage.setItem('NCToken', response.data.access_token);
        localStorage.setItem('NCAgencyId', response.data.agencyId);
        if (response.data.agencyName) {
          localStorage.setItem('NCAgencyName', response.data.agencyName);
        }
        if (response.data.userId) {
          localStorage.setItem('NCUserId', response.data.userId);
        }
      }

      logLoginEvent(igToken);
      login(ncSetupUserName);
      setLoading(false);
      if (policy) {
        navigate(`/selectclient?policy=${encodeURIComponent(policy)}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    })
    .catch((error: any) => {
      setLoading(false);
      const status = error?.response?.status;

      if (status === 401) {
        clearAuthStorage();
        setError('Your session has expired. Please sign in again.');
      } else if (status === 409) {
        setError('You are not registered with Nowcerts. Please register first.');
      } else {
        setError('Unknown error while verifying Nowcerts credentials.');
      }
    });
}

function logLoginEvent(igToken: string) {
  try {
    const ncToken = localStorage.getItem('NCToken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/log-login`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${igToken}`,
        'NCToken': ncToken,
      },
    }).catch(() => { /* fire-and-forget — silently ignore */ });
  } catch { /* never break login flow */ }
}

function decodePolicyParam(raw: string): string {
  if (!raw) return raw;
  try {
    const decoded = atob(raw);
    if (/^[\x20-\x7E]+$/.test(decoded)) return decoded;
  } catch {}
  return raw;
}

function clearAuthStorage() {
  try {
    localStorage.removeItem('igtoken');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('userName');
    localStorage.removeItem('RoleId');
    localStorage.removeItem('AvatarName');
    localStorage.removeItem('NowcertsAgentId');
    localStorage.removeItem('NCToken');
    localStorage.removeItem('NCAgencyId');
    localStorage.removeItem('NCUserId');
    localStorage.removeItem('SocialLogin');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
  } catch {
  }
}
